/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { useHistory, Link } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Grid,
  Typography,
  Divider,
  Button,
  InputAdornment,
  Modal,
  Box,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles, createTheme, ThemeProvider } from "@material-ui/core/styles";
import logo from "../../assets/img/s10Logo.png";
import authImage from '../../assets/img/auth.png';
import MyTextField from "../../components/MyTextField";
import MySnackbar from "../../components/MySnackbar";
import { recoverPasswordRequest } from "../../api/authApi";

const theme = createTheme({
  palette: {
    primary: {
      main: "#10617B",
    },
    background: {
      default: "#f5f5f5",
    },
  },
});

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100vh",
    margin: 0,
  },
  leftColumn: {
    backgroundColor: "#E8F3F7",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  formContainer: {
    maxWidth: "320px",
    width: "100%",
  },
  logo: {
    width: "140px",
    height: "140px",
    marginBottom: ".3rem",
  },
  title: {
    color: "#3C8DBD",
    fontSize: "32px",
    fontWeight: 700,
    marginBottom: "0.5rem",
    lineHeight: "1.167",
    letterSpacing: "0em",
  },
  subTitle: {
    color: "black",
    margin: 0,
    letterSpacing: "0.0075em",
    fontFamily: "Roboto",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "normal",
  },
  divider: {
    width: "100%",
    margin: "1.5rem 0",
    backgroundColor: theme.palette.grey[300],
  },
  button: {
    backgroundColor: "#10617B",
    marginTop: "3rem",
    "&:disabled": {
      backgroundColor: "#b0b0b0",
    },
  },
  rightColumn: {
    backgroundImage: `url(${require("../../assets/img/loginDefaultImage.svg").default})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundColor: theme.palette.primary.dark,
    display: "none",
    [theme.breakpoints.up("md")]: {
      display: "block",
    },
  },
  loadingOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(255,255,255,0.7)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1300,
  },
}));

export default function RecoverPassword() {
  const classes = useStyles();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    const lastAttempt = localStorage.getItem("lastPasswordRecoveryAttempt");
    if (lastAttempt) {
      const elapsed = Date.now() - parseInt(lastAttempt, 10);
      const remaining = Math.max(0, 60 - Math.floor(elapsed / 1000));
      if (remaining > 0) {
        setCountdown(remaining);
      } else {
        localStorage.removeItem("lastPasswordRecoveryAttempt");
      }
    }
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && localStorage.getItem("lastPasswordRecoveryAttempt")) {
      localStorage.removeItem("lastPasswordRecoveryAttempt");
    }
  }, [countdown]);

  const recoverPasswordHandle = async (values: any) => {
    const { username } = values;
    if (!username) {
      setErrorMsg("Email es requerido.");
      return;
    }

    const lastAttempt = localStorage.getItem("lastPasswordRecoveryAttempt");
    if (lastAttempt) {
      const elapsed = Date.now() - parseInt(lastAttempt, 10);
      if (elapsed < 60000) {
        const remainingSec = Math.ceil((60000 - elapsed) / 1000);
        setCountdown(remainingSec);
        setErrorMsg(`Debes esperar ${remainingSec} segundo(s) antes de reintentar.`);
        return;
      }
    }

    setLoading(true);
    try {
      const { data } = await recoverPasswordRequest(username);
      if (data.Type === 0) {
        localStorage.setItem("lastPasswordRecoveryAttempt", Date.now().toString());
        setCountdown(60);
        setSuccess(true);
      } else {
        setErrorMsg(data.Message);
      }
    } catch (error) {
      setErrorMsg("Error al enviar la solicitud. Intente más tarde.");
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: { username: "" },
    validationSchema: Yup.object({
      username: Yup.string()
        .email("El email no es válido")
        .required("El Email es Obligatorio"),
    }),
    onSubmit: recoverPasswordHandle,
  });

  const handleCloseModal = () => {
    setSuccess(false);
    history.push("/");
  };

  return (
    <ThemeProvider theme={theme}>
      <Grid container className={classes.root}>
        <Grid item xs={12} md={6} className={classes.leftColumn}>
          <div className={classes.formContainer}>
            <img src={logo} alt="Logo" className={classes.logo} />
            <Typography variant="h3" className={classes.title}>
              ¡Bienvenido!
            </Typography>
            <Typography variant="h6" className={classes.subTitle}>
              Portal Tablero - Recuperar contraseña
            </Typography>
            <Divider className={classes.divider} />
            <Typography
              variant="body1"
              align="left"
              style={{ width: "100%", marginBottom: "1rem", fontSize: "12px" }}
            >
              Ingresa tu correo afiliado para recuperar contraseña
            </Typography>

            <form onSubmit={formik.handleSubmit}>
              <MyTextField
                id="username"
                label="Usuario"
                type="text"
                name="username"
                value={formik.values.username}
                onChange={formik.handleChange}
                error={formik.touched.username && Boolean(formik.errors.username)}
                helperText={formik.touched.username && formik.errors.username}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" style={{ color: "#191919" }}>
                      <i className="fas fa-envelope" style={{ color: "#999", fontSize: '1.5rem' }}></i>
                    </InputAdornment>
                  ),
                }}
              />

              <Typography variant="body2" style={{ marginTop: "10px" }}>
                <Link to="/" color="primary">
                  Ingresa aquí
                </Link>
              </Typography>

              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                className={classes.button}
                disabled={loading || countdown > 0}
              >
                {loading ? (
                  <CircularProgress size={24} />
                ) : countdown > 0 ? (
                  `Espera ${countdown}s`
                ) : (
                  "Recuperar"
                )}
              </Button>
            </form>
          </div>
        </Grid>

        <Grid item md={6} className={classes.rightColumn}>
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 0,
            }}
          >
            <img
              src={authImage}
              alt="Ilustración auth"
              style={{
                maxWidth: '100%',
                maxHeight: '90%',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
              }}
            />
          </Box>
        </Grid>
      </Grid>

      <MySnackbar
        open={errorMsg !== ""}
        autoHideDuration={6000}
        msg={errorMsg}
        severity="error"
        onClose={() => setErrorMsg("")}
      />

      <Modal open={success} onClose={handleCloseModal}>
        <Box
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            backgroundColor: "white",
            boxShadow: "0px 3px 5px -1px rgba(0,0,0,0.2)",
            padding: "16px 32px 24px 32px",
            borderRadius: 8,
            outline: "none",
          }}
        >
          <Typography variant="h6" component="h2" gutterBottom>
            Correo enviado exitosamente
          </Typography>
          <Typography variant="body1" style={{ marginBottom: 16 }}>
            Hemos enviado un correo electrónico a tu dirección de correo registrada con una
            contraseña temporal. Por favor, revisa tu bandeja de entrada.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCloseModal}
            style={{ marginTop: 8 }}
          >
            Cerrar
          </Button>
        </Box>
      </Modal>

      {loading && (
        <div className={classes.loadingOverlay}>
          <CircularProgress />
        </div>
      )}
    </ThemeProvider>
  );
}